import { useState, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
} from '@mui/material';
import MDBox from 'src/components/md/MDBox';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import MDTypography from 'src/components/md/MDTypography';
import FamousSalesOrderModal from './FamousSalesOrderModal';
import useFamousSalesOrders, {
  FamousSalesOrderDTO,
} from 'src/api/temp/famous/useFamousSalesOrders';

const COLUMN_WIDTHS = [
  '5%', // #
  '15%', // Famous Order ID
  '10%', // Invoice Type
  '42%', // EDI
  '12%', // Order Date
];

const FamousSalesOrdersTable: React.FC = () => {
  // const [searchParams, setSearchParams] = useSearchParams();

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedFamousSalesOrder, setSelectedFamousSalesOrder] =
    useState<FamousSalesOrderDTO>();

  // Use a ref to track if we're in the middle of a pagination change
  const isPaginating = useRef(false);

  const { data, isLoading, error, refetch } = useFamousSalesOrders({
    page: pageIndex,
    pageSize: pageSize,
  });

  // Handle row selection with URL update
  const handleSelectSalesOrder = (order: FamousSalesOrderDTO) => {
    setSelectedFamousSalesOrder(order);
  };

  // Handle page change - fixed to update the pageIndex correctly
  const handlePageChange = (_: unknown, newPage: number) => {
    // MUI pagination is 0-based, but our API is 1-based
    isPaginating.current = true;
    setPageIndex(newPage + 1);
  };

  const columns: ColumnDef<FamousSalesOrderDTO>[] = [
    {
      accessorKey: 'index',
      header: '#',
      cell: (info) => {
        const rowIndex = info.row.index;
        const absoluteIndex = (pageIndex - 1) * pageSize + rowIndex + 1;
        return <MDTypography variant="caption">{absoluteIndex}</MDTypography>;
      },
      size: 50,
      minSize: 50,
      maxSize: 50,
    },
    {
      accessorKey: 'famousSalesOrderId',
      header: 'Famous Order ID',
      cell: (info) => {
        const orderId = info.getValue() as string;
        return (
          <MDTypography variant="caption" fontWeight="medium">
            {orderId || '-'}
          </MDTypography>
        );
      },
      minSize: 120,
      maxSize: 180,
    },
    // {
    //   id: 'customer',
    //   header: 'Customer',
    //   cell: (info) => {
    //     const row = info.row.original;
    //     const customerName = row.orderDetails?.customerName;

    //     if (customerName) {
    //       return (
    //         <MDBox>
    //           <MDTypography variant="caption" fontWeight="medium">
    //             {customerName}
    //           </MDTypography>
    //           {row.orderDetails?.customerCity && (
    //             <MDTypography
    //               variant="caption"
    //               color="text.secondary"
    //               sx={{ display: 'block', fontSize: '0.7rem' }}
    //             >
    //               {row.orderDetails.customerCity}
    //             </MDTypography>
    //           )}
    //         </MDBox>
    //       );
    //     }

    //     return missingMessage;
    //   },
    //   minSize: 150,
    //   maxSize: 250,
    // },
    {
      id: 'ediType',
      header: 'Invoice Type',
      cell: (info) => {
        const row = info.row.original;
        const { ediInvoiceType } = row;
        return (
          <MDTypography variant="caption">{ediInvoiceType || '-'}</MDTypography>
        );
      },
      minSize: 20,
      maxSize: 30,
    },
    {
      id: 'ediStatus',
      header: 'EDI',
      cell: (info) => {
        const row = info.row.original;
        const {
          edi810SentAt,
          edi810Status,
          ediInvoiceSentAt,
          ediInvoiceStatus,
        } = row;

        if (
          (edi810SentAt && edi810Status) ||
          (ediInvoiceSentAt && ediInvoiceStatus)
        ) {
          const sentDateValue = ediInvoiceSentAt || edi810SentAt;
          if (!sentDateValue) {
            return (
              <Chip
                label="Not Sent"
                color="warning"
                size="small"
                variant="outlined"
              />
            );
          }
          const sentDate = new Date(sentDateValue);
          const dateStr = sentDate.toLocaleDateString();
          const timeStr = sentDate.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          });
          const transactionId = ediInvoiceStatus || edi810Status;

          return (
            <MDBox>
              <Chip
                label={'Sent'}
                color="success"
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  if (transactionId) {
                    window.open(
                      `https://ui.orderful.com/transactions/${transactionId}`,
                      '_blank',
                    );
                  }
                }}
                sx={{ cursor: transactionId ? 'pointer' : 'default' }}
              />
              <MDTypography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontSize: '0.7rem', mt: 0.5 }}
              >
                {dateStr} at {timeStr}
              </MDTypography>
            </MDBox>
          );
        }

        return (
          <Chip
            label="Not Sent"
            color="warning"
            size="small"
            variant="outlined"
          />
        );
      },
      minSize: 100,
      maxSize: 130,
    },

    {
      accessorKey: 'createdAt',
      header: 'Imported',
      cell: (info) => {
        const value = info.getValue() as Date | string;

        const date = typeof value === 'string' ? new Date(value) : value;
        const dateStr = date.toLocaleDateString();
        const timeStr = date.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        });

        return (
          <MDBox>
            <MDTypography variant="caption" sx={{ display: 'block' }}>
              {dateStr}
            </MDTypography>
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', fontSize: '0.7rem' }}
            >
              {timeStr}
            </MDTypography>
          </MDBox>
        );
      },
      size: 120,
      minSize: 120,
      maxSize: 120,
    },
  ];

  const table = useReactTable({
    data: data?.edges || [],
    columns,
    pageCount: data?.pageInfo?.totalPages || -1,
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageIndex - 1,
        pageSize,
      },
    },
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? updater({ pageIndex: pageIndex - 1, pageSize })
          : updater;
      setPageIndex(next.pageIndex + 1);
      setPageSize(next.pageSize);
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (isLoading && !isPaginating.current) {
    return (
      <MDBox display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </MDBox>
    );
  }

  if (error) {
    return (
      <MDBox display="flex" justifyContent="center" p={3}>
        <ErrorMessage error={error} />
      </MDBox>
    );
  }

  return (
    <MDBox>
      <TableContainer sx={{ boxShadow: 'none' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header, index) => {
                  return (
                    <TableCell
                      key={header.id}
                      sx={{
                        position: 'relative',
                        textAlign: 'left',
                        opacity: 0.7,
                        background: 'transparent',
                        color: 'rgb(123, 128, 154)',
                        boxShadow: 'none',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        width: COLUMN_WIDTHS[index],
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : header.column.columnDef.header instanceof Function
                          ? header.column.columnDef.header(header.getContext())
                          : header.column.columnDef.header}
                    </TableCell>
                  );
                }),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
                onClick={() => handleSelectSalesOrder(row.original)}
              >
                {row.getVisibleCells().map((cell, index) => {
                  return (
                    <TableCell
                      key={cell.id}
                      sx={{
                        color: '#7b809a',
                        fontSize: '0.875rem',
                        fontWeight: '400',
                        width: COLUMN_WIDTHS[index],
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {cell.column.columnDef.cell instanceof Function
                        ? cell.column.columnDef.cell(cell.getContext())
                        : String(cell.getValue() ?? '')}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={data?.pageInfo?.totalCount || 0}
        page={pageIndex - 1}
        onPageChange={handlePageChange}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          setPageSize(Number(e.target.value));
          setPageIndex(1);
        }}
        nextIconButtonProps={{
          disabled: !data?.pageInfo?.hasNextPage,
        }}
        backIconButtonProps={{
          disabled: !data?.pageInfo?.hasPreviousPage,
        }}
      />

      {selectedFamousSalesOrder && (
        <FamousSalesOrderModal
          famousSalesOrder={selectedFamousSalesOrder}
          onClose={(shouldRefetch) => {
            if (shouldRefetch) {
              refetch();
            }
            setSelectedFamousSalesOrder(undefined);
          }}
        />
      )}
    </MDBox>
  );
};

export default FamousSalesOrdersTable;
